import { useMemo, useState } from 'react';
import { useTheme } from '@/store/theme-context';
import { cn } from '@/lib/cn';
import type { CatalogCategory, CatalogResponse, DotplotUser } from '@/types/analytics';
import {
  categoryColor,
  dotSizePx,
  formatDayLabel,
  isAutomationOnly,
  isWeekendUTC,
  sortedEventBreakdown,
  userDisplayName,
  userInitials,
} from '@/lib/analytics/dotplot';
import { AutomationDot } from './AutomationDot';

interface DotPlotGridProps {
  days: string[];
  users: DotplotUser[];
  catalog: CatalogResponse;
  /** When provided, the user cell becomes a button that drills into a focus view. */
  onSelectUser?: (userId: string) => void;
}

interface TipState {
  user: DotplotUser;
  day: string;
  /** Anchor rect of the hovered/focused dot (viewport coords). */
  rect: DOMRect;
}

/**
 * One activity grid per catalog category (Runs, Chat, Tools, …) stacked
 * vertically. Rows = users (same cohort order across every grid so a single
 * user can be scanned top-to-bottom through each activity type), columns =
 * days. Each grid is monochrome in its category color; dot size buckets by
 * that category's daily count. The onboarding day is marked by tinting that
 * user's day cell (not by ringing a dot), so it reads consistently in every
 * grid whether or not the user was active in that category that day.
 */
export function DotPlotGrid({ days, users, catalog, onSelectUser }: DotPlotGridProps) {
  return (
    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-6 xl:grid-cols-2">
      {catalog.categories.map((category) => (
        <CategoryGrid
          key={category.key}
          days={days}
          users={users}
          category={category}
          onSelectUser={onSelectUser}
        />
      ))}
    </div>
  );
}

function CategoryGrid({
  days,
  users,
  category,
  onSelectUser,
}: {
  days: string[];
  users: DotplotUser[];
  category: CatalogCategory;
  onSelectUser?: (userId: string) => void;
}) {
  const { resolvedTheme } = useTheme();
  const [tip, setTip] = useState<TipState | null>(null);
  const color = categoryColor(category, resolvedTheme) ?? 'hsl(var(--muted-foreground))';

  const { activeUsers, totalEvents } = useMemo(() => {
    let active = 0;
    let total = 0;
    for (const user of users) {
      let sum = 0;
      for (const cell of Object.values(user.cells)) {
        sum += cell.byCategory?.[category.key] ?? 0;
      }
      if (sum > 0) active += 1;
      total += sum;
    }
    return { activeUsers: active, totalEvents: total };
  }, [users, category.key]);

  return (
    <section className="space-y-2">
      <header className="flex items-center gap-2">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ background: color }}
        />
        <h3 className="text-sm font-semibold text-foreground">{category.label}</h3>
        <span className="text-xs text-muted-foreground">
          {activeUsers} active {activeUsers === 1 ? 'user' : 'users'} ·{' '}
          {totalEvents.toLocaleString()} events
        </span>
      </header>

      <div className="relative">
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="border-separate border-spacing-0">
            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-card px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                >
                  User
                </th>
                {days.map((day) => (
                  <th
                    key={day}
                    scope="col"
                    className={cn(
                      'h-10 min-w-[26px] px-0 py-1 text-center align-bottom text-[10px] font-medium text-muted-foreground',
                      isWeekendUTC(day) && 'bg-muted/50',
                    )}
                  >
                    <span className="inline-block origin-center -rotate-45 whitespace-nowrap">
                      {formatDayLabel(day)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.userId} className="group">
                  <th
                    scope="row"
                    title={user.userId}
                    className="sticky left-0 z-10 max-w-[220px] bg-card px-3 py-1.5 text-left font-normal group-hover:bg-muted/40"
                  >
                    <UserLabel user={user} onSelectUser={onSelectUser} />
                  </th>
                  {days.map((day) => {
                    const cell = user.cells[day];
                    const weekend = isWeekendUTC(day);
                    const count = cell?.byCategory?.[category.key] ?? 0;
                    const onboarding = !!cell?.firstSeen;
                    const tdClass = cn(
                      'h-[26px] min-w-[26px] text-center',
                      onboarding
                        ? 'bg-amber-400/20'
                        : weekend && 'bg-muted/50',
                    );
                    if (count <= 0) {
                      return (
                        <td
                          key={day}
                          className={tdClass}
                          title={onboarding ? 'Onboarding day' : undefined}
                        >
                          {cell && isAutomationOnly(cell) && (
                            <AutomationDot
                              cell={cell}
                              day={day}
                              title={userDisplayName(user)}
                            />
                          )}
                        </td>
                      );
                    }
                    const size = dotSizePx(count);
                    return (
                      <td key={day} className={tdClass}>
                        <span
                          tabIndex={0}
                          role="img"
                          aria-label={`${userDisplayName(user)}, ${day}: ${count} ${category.label} event${count === 1 ? '' : 's'}${onboarding ? ', onboarding day' : ''}`}
                          onMouseEnter={(e) =>
                            setTip({ user, day, rect: e.currentTarget.getBoundingClientRect() })
                          }
                          onMouseLeave={() => setTip(null)}
                          onFocus={(e) =>
                            setTip({ user, day, rect: e.currentTarget.getBoundingClientRect() })
                          }
                          onBlur={() => setTip(null)}
                          className="inline-block cursor-default rounded-full align-middle outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          style={{ width: size, height: size, background: color }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {tip && <DotTooltip tip={tip} category={category} />}
      </div>
    </section>
  );
}

function UserLabel({
  user,
  onSelectUser,
}: {
  user: DotplotUser;
  onSelectUser?: (userId: string) => void;
}) {
  const avatar = user.avatarUrl ? (
    <img
      src={user.avatarUrl}
      alt=""
      className="h-6 w-6 shrink-0 rounded-full object-cover"
    />
  ) : (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
      {userInitials(user)}
    </span>
  );

  if (!onSelectUser) {
    return (
      <div className="flex items-center gap-2">
        {avatar}
        <span className="truncate text-xs text-foreground">{userDisplayName(user)}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelectUser(user.userId)}
      className="flex w-full items-center gap-2 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
      title={`View ${userDisplayName(user)}'s activity`}
    >
      {avatar}
      <span className="truncate text-xs text-foreground group-hover:underline">
        {userDisplayName(user)}
      </span>
    </button>
  );
}

/** Fixed-position tooltip anchored above (or below, if clipped) the dot. */
function DotTooltip({
  tip,
  category,
}: {
  tip: TipState;
  category: CatalogCategory;
}) {
  const cell = tip.user.cells[tip.day];
  if (!cell) return null;
  const count = cell.byCategory?.[category.key] ?? 0;
  const catEvents = new Set(category.events);
  const breakdown = sortedEventBreakdown(cell).filter(([event]) => catEvents.has(event));

  const placeBelow = tip.rect.top - 8 < 120;
  const top = placeBelow ? tip.rect.bottom + 8 : tip.rect.top - 8;

  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 max-w-[340px] -translate-x-1/2 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg"
      style={{
        left: tip.rect.left + tip.rect.width / 2,
        top,
        transform: `translate(-50%, ${placeBelow ? '0' : '-100%'})`,
      }}
    >
      <div className="font-medium text-foreground">{userDisplayName(tip.user)}</div>
      <div className="mt-0.5 text-muted-foreground">
        {tip.day} · {category.label}: {count} event{count === 1 ? '' : 's'}
        {cell.firstSeen && ' · ★ onboarding day'}
      </div>
      {breakdown.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5">
          {breakdown.map(([event, n]) => (
            <div key={event} className="contents">
              <dt className="truncate text-muted-foreground">{event}</dt>
              <dd className="text-right font-mono tabular-nums text-foreground">{n}</dd>
            </div>
          ))}
        </dl>
      )}
      {(cell.automation ?? 0) > 0 && (
        <div className="mt-1 text-muted-foreground">
          ◦ automation: {cell.automation}
        </div>
      )}
    </div>
  );
}

export default DotPlotGrid;
